// Runs a macro (see macros.ts) once the reply it belongs to has actually been sent.
//
// Called from the outbox's reply success path (outbox-jobs.ts), so it never runs on Undo or on
// a failed send. It never throws: the reply is already out, and a macro that can't run must not
// look like the reply failed. A refusal is shown by the same toast as a button click (ticket put
// back, Retry), and a reminder that can't be set says so and that the reply WAS sent.

import type { QueryClient } from '@tanstack/react-query';
import type { TicketAction } from './api';
import { toTicketActionCall, type AfterSend } from './macros';
import { reminderAtDays, describeWhen } from './reminders';
import { makeActionContext, runTicketAction } from './run-ticket-action';
import { supabase } from './supabase';
import { toastFailure, toastSuccess } from './toast';

export interface AfterSendRun {
  ticketId: string;
  subject: string | null;
  /** The sender, as the dispatcher needs it (optimistic claims, "my" tickets). */
  actorSlackId: string | null;
  afterSend: AfterSend;
}

/** Resolves `true` when the macro's action went through, `false` when it didn't (already shown
 *  to the person). Never rejects. */
export async function runAfterSend(
  qc: QueryClient,
  run: AfterSendRun,
  now: () => Date = () => new Date(),
): Promise<boolean> {
  const call = toTicketActionCall(run.afterSend);

  if (call.kind === 'action') {
    return runTicketAction(
      qc,
      makeActionContext(qc, run.actorSlackId),
      run.ticketId,
      call.action as TicketAction,
      call.payload,
      { subject: run.subject },
    );
  }

  // "Remind me in N days": no reply from the client by then is the whole point.
  try {
    const at = reminderAtDays(now(), call.days);
    const { error } = await supabase.rpc('set_ticket_reminder', {
      p_ticket_id: run.ticketId,
      p_remind_at: at.toISOString(),
      p_only_if_no_reply: true,
    });
    if (error) throw error;
    qc.invalidateQueries({ queryKey: ['my-reminders'] });
    qc.invalidateQueries({ queryKey: ['reminder-tickets'] });
    toastSuccess(`Reminder set for ${describeWhen(at, now())}`, 'If the client has not replied by then.');
    return true;
  } catch (err) {
    toastFailure(err, 'Your reply was sent, but the reminder could not be set');
    return false;
  }
}
